import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { 
    Activity, 
    Mail, 
    Lock, 
    Loader2, 
    AlertCircle,
    Eye,
    EyeOff,
    Shield,
    CheckCircle2,
    Globe,
    ArrowRight,
    ArrowLeft,
    Sparkles
} from 'lucide-react';
import { toast } from 'sonner';
import LanguageSelector from '../components/LanguageSelector';

const LoginPage = () => {
    const { t, i18n } = useTranslation();
    const isRTL = i18n.language === 'ar';
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [rememberMe, setRememberMe] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const { login, initiateGoogleLogin } = useAuth();
    const navigate = useNavigate();

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);

        try {
            await login(email, password, rememberMe);
            toast.success(t('notifications.loginSuccess'));
            navigate('/dashboard');
        } catch (err) {
            // Fallback: the same form also accepts admin accounts.
            // Admin credentials live in a separate collection (/api/admin/login),
            // so if the regular user login fails, try logging in as an admin.
            try {
                await axios.post(
                    `${API}/admin/login`,
                    { email, password, remember_me: rememberMe },
                    { withCredentials: true }
                );
                toast.success(t('notifications.loginSuccess'));
                // Full navigation so AdminProvider mounts and picks up the cookie session.
                window.location.href = '/admin/dashboard';
                return;
            } catch (adminErr) {
                const message = err.response?.data?.detail || t('errors.loginFailed');
                setError(message);
                toast.error(message);
            }
        } finally {
            setLoading(false);
        }
    };

    const handleSocialLogin = (provider) => {
        if (provider === 'Google') {
            initiateGoogleLogin();
        } else {
            toast.info(`${provider} sign-in coming soon!`);
        }
    };

    const ArrowIcon = isRTL ? ArrowLeft : ArrowRight;

    return (
        <div className="min-h-screen flex" data-testid="login-page">
            {/* Left Panel - Medical Background */}
            <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
                {/* DNA/Medical Pattern Background */}
                <div className="absolute inset-0 bg-gradient-to-br from-[#0A1628] via-[#1E3A5F] to-[#0D2137]">
                    {/* Animated DNA pattern overlay */}
                    <div className="absolute inset-0 opacity-10">
                        <svg className="w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none">
                            <defs>
                                <pattern id="dna-pattern-login" x="0" y="0" width="20" height="20" patternUnits="userSpaceOnUse">
                                    <circle cx="10" cy="10" r="1.5" fill="currentColor" className="text-teal-400"/>
                                    <circle cx="5" cy="5" r="1" fill="currentColor" className="text-sky-400"/>
                                    <circle cx="15" cy="15" r="1" fill="currentColor" className="text-sky-400"/>
                                    <line x1="5" y1="5" x2="15" y2="15" stroke="currentColor" strokeWidth="0.3" className="text-teal-400"/>
                                </pattern>
                            </defs>
                            <rect width="100" height="100" fill="url(#dna-pattern-login)"/>
                        </svg>
                    </div>
                    
                    {/* Glowing orbs */}
                    <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-teal-500/20 rounded-full blur-3xl animate-pulse" />
                    <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-sky-500/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
                </div>
                
                {/* Content */}
                <div className="relative z-10 flex flex-col justify-between p-12 text-white">
                    {/* Logo */}
                    <Link to="/" className="flex items-center gap-3 group">
                        <div className="w-12 h-12 rounded-2xl bg-white/10 backdrop-blur-sm border border-white/20 flex items-center justify-center group-hover:bg-white/20 transition-all">
                            <Activity className="w-7 h-7 text-teal-400" />
                        </div>
                        <div>
                            <span className="text-xl font-bold">{t('app.name')}</span>
                            <span className="block text-xs text-sky-300">My Medical Results</span>
                        </div>
                    </Link>
                    
                    {/* Main content */}
                    <div className="space-y-8">
                        <div>
                            <h1 className="text-4xl font-bold leading-tight mb-4">
                                {t('auth.welcomeBack')}<br />
                                <span className="text-transparent bg-clip-text bg-gradient-to-r from-teal-400 to-sky-400">
                                    {isRTL ? 'مركزك الصحي' : 'Your Health Hub'}
                                </span>
                            </h1>
                            <p className="text-lg text-slate-300 max-w-md leading-relaxed">
                                {isRTL 
                                    ? 'اطلع على نتائج تحاليلك، تتبع مؤشراتك الصحية، واحصل على تحليلات الذكاء الاصطناعي في أي وقت.'
                                    : 'Access your lab results, track health trends, and get AI-powered insights anytime.'}
                            </p>
                        </div>
                        
                        {/* Features */}
                        <div className="space-y-4">
                            {[
                                { icon: Sparkles, text: isRTL ? 'تحليل فوري بالذكاء الاصطناعي' : 'Instant AI analysis of your results' },
                                { icon: Shield, text: isRTL ? 'بياناتك آمنة دائماً' : 'Your data is always secure' },
                                { icon: Activity, text: isRTL ? 'تتبع رحلتك الصحية' : 'Track your health journey' }
                            ].map((item, i) => (
                                <div key={i} className="flex items-center gap-3 text-slate-300">
                                    <div className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center">
                                        <item.icon className="w-4 h-4 text-teal-400" />
                                    </div>
                                    <span>{item.text}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                    
                    {/* Trust badges */}
                    <div className="flex items-center gap-6">
                        <div className="flex items-center gap-2 text-sm text-slate-400">
                            <Shield className="w-4 h-4" />
                            <span>256-bit SSL</span>
                        </div>
                        <div className="flex items-center gap-2 text-sm text-slate-400">
                            <CheckCircle2 className="w-4 h-4" />
                            <span>HIPAA Ready</span>
                        </div>
                        <div className="flex items-center gap-2 text-sm text-slate-400">
                            <Globe className="w-4 h-4" />
                            <span>GDPR Compliant</span>
                        </div>
                    </div>
                </div>
            </div>

            {/* Right Panel - Login Form */}
            <div className="flex-1 flex items-center justify-center p-6 sm:p-8 lg:p-12 bg-gradient-to-br from-slate-50 via-white to-sky-50/30">
                <div className="w-full max-w-md">
                    {/* Language Selector */}
                    <div className="flex justify-end mb-4">
                        <LanguageSelector />
                    </div>
                    
                    {/* Mobile Logo */}
                    <div className="lg:hidden flex justify-center mb-8">
                        <Link to="/" className="flex items-center gap-3">
                            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-[#1E3A5F] to-[#0D2137] flex items-center justify-center shadow-xl">
                                <Activity className="w-7 h-7 text-teal-400" />
                            </div>
                            <div>
                                <span className="text-xl font-bold text-slate-800">{t('app.name')}</span>
                                <span className="block text-xs text-slate-500">My Medical Results</span>
                            </div>
                        </Link>
                    </div>

                    {/* Login Card */}
                    <Card className="border-0 shadow-2xl shadow-slate-200/50 backdrop-blur-sm">
                        <CardContent className="p-8">
                            <div className="text-center mb-8">
                                <h2 className="text-2xl font-bold text-slate-800 mb-2">{t('auth.login')}</h2>
                                <p className="text-slate-500">{t('auth.loginSubtitle')}</p>
                            </div>

                            <form onSubmit={handleSubmit} className="space-y-5">
                                {error && (
                                    <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm" data-testid="login-error">
                                        <AlertCircle className="w-4 h-4 flex-shrink-0" />
                                        {error}
                                    </div>
                                )}
                                
                                {/* Email */}
                                <div className="space-y-2">
                                    <Label htmlFor="email" className="text-slate-700">{t('auth.email')}</Label>
                                    <div className="relative">
                                        <Mail className={`absolute ${isRTL ? 'right-3' : 'left-3'} top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400`} />
                                        <Input
                                            id="email"
                                            type="email"
                                            placeholder={t('auth.enterEmail')}
                                            value={email}
                                            onChange={(e) => setEmail(e.target.value)}
                                            required
                                            className={`${isRTL ? 'pr-10' : 'pl-10'} h-12 rounded-xl border-slate-200 focus:border-teal-500 focus:ring-teal-500/20`}
                                            data-testid="login-email-input"
                                        />
                                    </div>
                                </div>

                                {/* Password */}
                                <div className="space-y-2">
                                    <div className="flex items-center justify-between">
                                        <Label htmlFor="password" className="text-slate-700">{t('auth.password')}</Label>
                                        <Link to="/forgot-password" className="text-sm text-teal-600 hover:underline">
                                            {t('auth.forgotPassword')}
                                        </Link>
                                    </div>
                                    <div className="relative">
                                        <Lock className={`absolute ${isRTL ? 'right-3' : 'left-3'} top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400`} />
                                        <Input
                                            id="password"
                                            type={showPassword ? 'text' : 'password'}
                                            placeholder="••••••••"
                                            value={password}
                                            onChange={(e) => setPassword(e.target.value)}
                                            required
                                            className={`${isRTL ? 'pr-10 pl-10' : 'pl-10 pr-10'} h-12 rounded-xl border-slate-200 focus:border-teal-500 focus:ring-teal-500/20`}
                                            data-testid="login-password-input"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowPassword(!showPassword)}
                                            className={`absolute ${isRTL ? 'left-3' : 'right-3'} top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600`}
                                        >
                                            {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                                        </button>
                                    </div>
                                </div>

                                {/* Remember Me */}
                                <div className="flex items-center justify-between">
                                    <label className="flex items-center gap-2 cursor-pointer group">
                                        <input
                                            type="checkbox"
                                            checked={rememberMe}
                                            onChange={(e) => setRememberMe(e.target.checked)}
                                            className="w-4 h-4 rounded border-slate-300 text-teal-500 focus:ring-teal-500/20 cursor-pointer"
                                            data-testid="remember-me-checkbox"
                                        />
                                        <span className="text-sm text-slate-600 group-hover:text-slate-800">{t('auth.rememberMe')}</span>
                                    </label>
                                </div>

                                {/* Submit Button */}
                                <Button
                                    type="submit"
                                    disabled={loading}
                                    className="w-full h-12 bg-teal-500 hover:bg-teal-600 text-white rounded-xl font-medium shadow-lg shadow-teal-500/25 transition-all hover:scale-[1.02] hover:-translate-y-0.5"
                                    data-testid="login-submit-btn"
                                >
                                    {loading ? (
                                        <>
                                            <Loader2 className="w-5 h-5 animate-spin ltr:mr-2 rtl:ml-2" />
                                            {isRTL ? 'جاري تسجيل الدخول...' : 'Signing In...'}
                                        </>
                                    ) : (
                                        <>
                                            {t('auth.login')}
                                            <ArrowIcon className="w-5 h-5 ltr:ml-2 rtl:mr-2" />
                                        </>
                                    )}
                                </Button>

                                {/* Social Sign-in */}
                                <div className="relative">
                                    <div className="absolute inset-0 flex items-center">
                                        <span className="w-full border-t border-slate-200" />
                                    </div>
                                    <div className="relative flex justify-center text-xs uppercase">
                                        <span className="bg-white px-2 text-slate-500">{t('auth.signInWith')}</span>
                                    </div>
                                </div>

                                <div className="grid grid-cols-2 gap-3">
                                    <Button
                                        type="button"
                                        variant="outline"
                                        onClick={() => handleSocialLogin('Google')}
                                        className="h-12 rounded-xl border-slate-200 hover:bg-slate-50 hover:border-slate-300"
                                        data-testid="google-signin-btn"
                                    >
                                        <svg className="w-5 h-5 ltr:mr-2 rtl:ml-2" viewBox="0 0 24 24">
                                            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                                            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                                            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                                            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                                        </svg>
                                        {t('auth.google')}
                                    </Button>
                                    <Button
                                        type="button"
                                        variant="outline"
                                        onClick={() => handleSocialLogin('Microsoft')}
                                        className="h-12 rounded-xl border-slate-200 hover:bg-slate-50 hover:border-slate-300"
                                        data-testid="microsoft-signin-btn"
                                    >
                                        <svg className="w-5 h-5 ltr:mr-2 rtl:ml-2" viewBox="0 0 24 24">
                                            <path fill="#F25022" d="M1 1h10v10H1z"/>
                                            <path fill="#00A4EF" d="M1 13h10v10H1z"/>
                                            <path fill="#7FBA00" d="M13 1h10v10H13z"/>
                                            <path fill="#FFB900" d="M13 13h10v10H13z"/>
                                        </svg>
                                        {t('auth.microsoft')}
                                    </Button>
                                </div>
                            </form>

                            {/* Trust indicators */}
                            <div className="mt-6 pt-6 border-t border-slate-100">
                                <div className="flex items-center justify-center gap-4 text-xs text-slate-500">
                                    <div className="flex items-center gap-1">
                                        <Shield className="w-3 h-3" />
                                        <span>{isRTL ? 'مشفر' : 'Encrypted'}</span>
                                    </div>
                                    <div className="flex items-center gap-1">
                                        <CheckCircle2 className="w-3 h-3" />
                                        <span>HIPAA</span>
                                    </div>
                                    <div className="flex items-center gap-1">
                                        <Globe className="w-3 h-3" />
                                        <span>GDPR</span>
                                    </div>
                                    <div className="flex items-center gap-1">
                                        <Lock className="w-3 h-3" />
                                        <span>SSL</span>
                                    </div>
                                </div>
                            </div>

                            {/* Sign up link */}
                            <div className="mt-6 text-center">
                                <p className="text-slate-600">
                                    {t('auth.noAccount')}{' '}
                                    <Link to="/register" className="text-teal-600 font-medium hover:underline" data-testid="register-link">
                                        {t('auth.createAccount')}
                                    </Link>
                                </p>
                            </div>
                        </CardContent>
                    </Card>
                </div>
            </div>
        </div>
    );
};

export default LoginPage;
